import i18n from 'live/services/i18n'
import globalNotification from 'live/services/global-notification'
import _ from 'lodash'
import { Uom } from '../utils/type'
import { handleFetchErrors, entityReviver, entityReplacer } from './utils'
import React from 'react'

export const INTERVENTION_API = 'intervention'
export const WELL_API = 'well'
export const SECTION_API = 'section'
export const RUN_API = 'run'
export const RIG_TABLE_API = 'rigtable'
export const TUBULAR_API = 'tubulartype'
export const RIG_API = 'rig'

export const fetchEntityOptions: (entityUrl: string) => Promise<any> = _.memoize(entityUrl => {
    return fetch(`/services/plugin-static-data-manager/${entityUrl}/getOptions`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            return res.body
        })
        .catch(err => {
            globalNotification.error(i18n(`Error getting entity options.`))
            return null
        })
})

export const fetchUnitOptions: (type: string) => Promise<{ options: Uom[] }> = _.memoize(type => {
    return fetch(`/services/plugin-static-data-manager/query/getUnitsOptions/${type}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            return res.body
        })
        .catch(err => {
            // err
            globalNotification.error(i18n(`Unable to fetch unities for type.`))

            return null
        })
})

export function fetchAllEntities(assetId: number, start: number, end: number): Promise<any> {
    return fetch(`/services/plugin-static-data-manager/query/getEntitiesByTimestamp`, {
        method: 'POST',
        body: JSON.stringify({ rigId: assetId, start: start, end: end }),
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => {
            return r.text().then(data => {
                return JSON.parse(data, entityReviver)
            })
        })
        .catch(err => {
            console.log('error: ', err)
            globalNotification.error(i18n(`Unable to fetch entities for Rig ID`))
        })
}

/**
 * type: type of entity for query
 * args: map<string,string> field:value(string value)
 */
export function queryEntitiesByType(type: string, args: any): Promise<any> {
    return fetch(`/services/plugin-static-data-manager/${type}/queryEntities`, {
        method: 'POST',
        body: JSON.stringify(args),
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            return res.body
        })
        .catch(err => {
            // err
            globalNotification.error(i18n(`Unable to fetch entities for type.`))

            return null
        })
}

export function fetchEntityStartTimestamp(
    parentId: number,
    entityName: string,
    parentEntityName: string
): Promise<any> {
    return fetch(`/services/plugin-static-data-manager/query/getEntityStartTimestamp`, {
        method: 'POST',
        body: JSON.stringify({ parentId, entityName, parentEntityName }),
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .catch(e => ({
            body: null
        }))
        .then(res => {
            return res.body
        })
        .catch(err => {
            // err
            globalNotification.error(i18n(`Error getting start timestamp for entity.`))

            return null
        })
}

export function fetchEntityEndTimestamp(entityId: number, entityName: string, childEntityName: string): Promise<any> {
    return fetch(`/services/plugin-static-data-manager/query/getEntityEndTimestamp`, {
        method: 'POST',
        body: JSON.stringify({ entityId, entityName, childEntityName }),
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .catch(e => ({
            body: null
        }))
        .then(res => {
            return res.body
        })
        .catch(err => {
            // err
            globalNotification.error(i18n(`Error getting end timestamp for entity.`))

            return null
        })
}

export function fetchSingleEntity(entityName?: string, id?: number): Promise<any> {
    if (entityName === undefined || id === undefined) false
    return fetch(`/services/plugin-static-data-manager/${entityName}/${id}/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => {
            return r.text().then(data => {
                return JSON.parse(data, entityReviver)
            })
        })
        .catch(() => {
            globalNotification.error(i18n(`Unable to fetch entity.`))

            return null
        })
}

export function deleteSingleEntity(entityName: string, id: number): Promise<any> {
    return fetch(`/services/plugin-static-data-manager/${entityName}/${id}/`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => {
            if (r.status === 200) {
                globalNotification.success(i18n(`Entity deleted successfully.`))
            }
        })
        .catch(err => {
            err.json().then(r => {
                globalNotification.error(i18n(r.message))
            })
        })
}

export function createSingleEntity(entityName: string, body: any): Promise<any> {
    if (body.id) {
        globalNotification.error(i18n(`Invalid entity creation.`))

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject()
    }

    return fetch(`/services/plugin-static-data-manager/${entityName}/create`, {
        method: 'POST',
        body: JSON.stringify(body, entityReplacer),
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            globalNotification.success(i18n(`Entity created successfully.`))

            return res.body
        })
        .catch((err: Response) => {
            err.json()
                .then(json =>
                    globalNotification.error(
                        <>
                            <span>{i18n(`${i18n('Could not create')} ${entityName}:`)}</span>
                            {json.message && (
                                <>
                                    <br />
                                    <span>{i18n(json.message || '')}</span>
                                </>
                            )}
                        </>,
                        20
                    )
                )
                .catch(
                    globalNotification.error(
                        i18n(`${i18n('Unable to create entity')} ${entityName}:\n${err.status || ''}`)
                    )
                )

            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject()
        })
}

export function updateSingleEntity(entityName: string, body: any): Promise<any> {
    if (!body.id) {
        globalNotification.error(i18n(`Invalid entity creation.`))

        return Promise.reject()
    }

    return fetch(`/services/plugin-static-data-manager/${entityName}/update`, {
        method: 'POST',
        body: JSON.stringify(body, entityReplacer),
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            globalNotification.success(i18n(`Entity updated successfully.`))

            return res.body
        })
        .catch((err: Response) => {
            err.json()
                .then(json => globalNotification.error(i18n(`\n${json.message || ''}.`)))
                .catch(globalNotification.error(i18n(`\n${err.status || ''}.`)))

            return Promise.reject()
        })
}

export function fetchRigs(): Promise<any> {
    return fetch(`/services/plugin-liverig/assets/rig`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            return res.body
        })
        .catch(err => {
            globalNotification.error(i18n(`Unable to fetch assets for rig.`))
        })
}

export function fetchWells(): Promise<{ id: string; name: string }[]> {
    return fetch('/services/plugin-liverig/assets/well', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => r?.json && r.json().then(data => ({ status: r?.status, body: data })))
        .then(res => {
            return res?.body ?? []
        })
        .catch(err => {
            console.error(err)
            globalNotification.error(i18n(`Unable to fetch live wells.`))
            return []
        })
}

export const fetchRigIdFromName = rigName =>
    fetch(`/services/plugin-liverig/assets/list?name=${rigName}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => r.json && r.json())
        .then(rigs => String(rigs?.[0]?.extensionId) ?? undefined)
        .catch(err => {
            globalNotification.error(i18n(`Unable to get rig id from name`)`"${rigName}".`)
            return undefined
        })

export const fetchKpiGoalsWithFilter: (filter: any, rigId?: string) => Promise<any> = (filter, rigId = 'GLOBAL') => {
    return fetch(`/services/plugin-kpis-drilling/config/kpi_goals/${rigId}/filter`, {
        method: 'POST',
        body: JSON.stringify(filter),
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => r?.json && r.json().then(data => ({ status: r?.status, body: data })))
        .then(res => {
            return res?.body ?? []
        })
        .catch(err => {
            console.error(err)
            globalNotification.error(i18n(`Unable to fetch kpi goals with filter.`))
            return []
        })
}
